"use client";

import React, { createContext, useContext, useState } from "react";
import type { CandleData } from "@/components/blocks/advanced-chart/types";

interface ChartDataContextType {
    candleData: CandleData[];
    chartType: string;
    dataReady: boolean;
    currentPrice: number;
    setCandleData: (data: CandleData[]) => void;
    setChartType: (type: string) => void;
    setDataReady: (ready: boolean) => void;
    setCurrentPrice: (price: number) => void;
}

const ChartDataContext = createContext<ChartDataContextType | null>(null);

export function useChartData() {
    const ctx = useContext(ChartDataContext);
    if (!ctx) {
        throw new Error("useChartData must be used within a ChartDataProvider");
    }
    return ctx;
}

export function ChartDataProvider({ children }: { children: React.ReactNode }) {
    const [candleData, setCandleData] = useState<CandleData[]>([]);
    const [chartType, setChartType] = useState<string>("candlestick");
    const [dataReady, setDataReady] = useState(false);
    const [currentPrice, setCurrentPrice] = useState(0);

    // No mock data fallback — wait for real data from the backend

    // Log when real data arrives
    React.useEffect(() => {
        if (candleData.length > 0 && currentPrice > 0) {
            console.log('✅ Real chart data loaded:', {
                candles: candleData.length,
                currentPrice,
                latestCandle: candleData[candleData.length - 1]
            });
        }
    }, [candleData, currentPrice]);

    const value: ChartDataContextType = {
        candleData,
        chartType,
        dataReady,
        currentPrice,
        setCandleData,
        setChartType,
        setDataReady,
        setCurrentPrice,
    };

    return (
        <ChartDataContext.Provider value={value}>{children}</ChartDataContext.Provider>
    );
}



